import { type HTMLAttributes, type SyntheticEvent, useEffect, useRef, useState, type Ref } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material';
import type { TranslationKey } from '@/i18n/translation-key';
import { ContributorBusyNameField } from './contributor-busy-name-field';

export interface ActorOption {
  id: string;
  name: string;
  inputValue?: string;
}

export interface ContributorAutocompleteProps {
  options: ActorOption[];
  loading?: boolean;
  labelKey: TranslationKey;
  /** Label while create/add is in flight (defaults to common.name — mirrors media title during upload). */
  busyLabelKey?: TranslationKey;
  autocompleteId?: string;
  disabled?: boolean;
  onSelectExisting: (actor: ActorOption) => void;
  onCreateNew: (name: string) => void;
  inputRef?: Ref<HTMLInputElement>;
}

const filter = createFilterOptions<ActorOption>();

export const ContributorAutocomplete = ({
  options,
  loading = false,
  labelKey,
  busyLabelKey = 'common.name',
  autocompleteId = 'contributor-autocomplete',
  disabled = false,
  onSelectExisting,
  onCreateNew,
  inputRef,
}: ContributorAutocompleteProps) => {
  const t = useTranslations();
  const [value, setValue] = useState<ActorOption | null>(null);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const wasLoadingRef = useRef(false);

  // Only clear after a finished in-flight add — not when the parent uses this for a sync pick (crew).
  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      setPendingName(null);
      setValue(null);
    }
    wasLoadingRef.current = loading;
  }, [loading]);

  const handleChange = (_: SyntheticEvent, newValue: ActorOption | string | null) => {
    if (typeof newValue === 'string') {
      setPendingName(newValue);
      setValue(null);
      onCreateNew(newValue);
      return;
    }
    if (newValue?.inputValue) {
      setPendingName(newValue.inputValue);
      setValue(null);
      onCreateNew(newValue.inputValue);
      return;
    }
    if (newValue) {
      setPendingName(newValue.name);
      setValue(newValue);
      onSelectExisting(newValue);
    }
  };

  const displayName = pendingName ?? value?.name ?? '';
  // Lock the name as soon as a create/select starts (pendingName), so we never flash an empty
  // input while the parent flips `loading` — mirrors media keeping the title during upload.
  const showBusyState = Boolean(pendingName);

  const getFilterOptions = (opts: ActorOption[], params: FilterOptionsState<ActorOption>) => {
    const filtered = filter(opts, params);
    const { inputValue } = params;
    const isExisting = opts.some((option) => inputValue === option.name);
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        inputValue,
        id: '',
        name: `${t('common.add')} "${inputValue}"`,
      });
    }
    return filtered;
  };

  const getOptionLabel = (option: string | ActorOption) => {
    if (typeof option === 'string') {
      return option;
    }
    if (option.inputValue) {
      return option.inputValue;
    }
    return option.name || option.id || '';
  };

  const renderOption = (props: HTMLAttributes<HTMLLIElement> & { key: string }, option: ActorOption) => {
    const { key, ...optionProps } = props;
    return (
      <li key={key} {...optionProps}>
        {option.name}
      </li>
    );
  };

  if (showBusyState) {
    return (
      <ContributorBusyNameField label={t(busyLabelKey)} name={displayName} testId="contributor-autocomplete-busy" />
    );
  }

  return (
    <Autocomplete
      freeSolo
      clearOnBlur
      selectOnFocus
      handleHomeEndKeys
      id={autocompleteId}
      sx={{ mb: 1 }}
      loading={loading}
      value={value}
      options={options}
      onChange={handleChange}
      filterOptions={getFilterOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      disabled={disabled || loading}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="standard"
          label={t(labelKey)}
          disabled={disabled || loading}
          autoFocus
        />
      )}
    />
  );
};
